/**
 * 认证状态管理 Slice
 * 用户登录、注册、权限管理
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { tokenManager } from '../../utils/tokenManager';
import { apiSlice } from '../api/apiSlice';

// 用户角色枚举
export enum UserRole {
  ISSUER = 'issuer',
  EXCHANGE = 'exchange',
  ADMIN = 'admin',
}

// 用户状态枚举
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
}

// 用户信息接口
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  phone?: string;
  companyName?: string;
  avatar?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

// 认证状态接口
export interface AuthState {
  // 用户信息
  user: User | null;

  // 认证令牌
  token: string | null;
  refreshToken: string | null;

  // 状态标识
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // 用户偏好
  rememberMe: boolean;

  // 会话管理
  lastActivity: number | null;
  sessionTimeout: number;
}

// 使用统一token管理器初始化状态
const getInitialAuthState = (): AuthState => {
  const authInfo = tokenManager.getAuthInfo();

  return {
    user: authInfo.user,
    token: authInfo.token,
    refreshToken: authInfo.refreshToken,
    isAuthenticated: authInfo.isAuthenticated,
    isLoading: false,
    error: null,
    rememberMe: authInfo.rememberMe,
    lastActivity: null,
    sessionTimeout: 30 * 60 * 1000, // 30分钟
  };
};

// 初始状态
const initialState: AuthState = getInitialAuthState();

// 异步thunk - 快速登录（开发用）
export const quickLogin = createAsyncThunk(
  'auth/quickLogin',
  async (role: UserRole) => {
    // 模拟快速登录
    const mockUser: User = {
      id: `mock-user-${role}`,
      email: `${role}@greenbond.com`,
      name: role === UserRole.ISSUER ? '模拟发行人' : '模拟交易所',
      role: role,
      status: UserStatus.ACTIVE,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const mockToken = `mock_jwt_token_${Date.now()}`;
    const mockRefreshToken = `mock_refresh_token_${Date.now()}`;

    return {
      user: mockUser,
      token: mockToken,
      refreshToken: mockRefreshToken,
    };
  }
);

// 创建认证slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 设置加载状态
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // 设置错误信息
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // 清除错误
    clearError: state => {
      state.error = null;
    },

    // 设置记住我
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },

    // 更新最后活动时间
    updateLastActivity: state => {
      state.lastActivity = Date.now();
    },

    // 登出 - 使用统一token管理器
    logout: state => {
      // 使用统一token管理器清除所有认证信息
      tokenManager.clearAll();

      // 清除问卷草稿数据
      try {
        const draftIndex = JSON.parse(
          localStorage.getItem('questionnaire_drafts') || '[]'
        );
        draftIndex.forEach((draftId: string) => {
          localStorage.removeItem(`questionnaire_draft_${draftId}`);
        });
        localStorage.removeItem('questionnaire_drafts');
        console.log('已清除所有问卷草稿');
      } catch (error) {
        console.error('清除问卷草稿失败:', error);
      }

      // 重置Redux状态
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.lastActivity = null;
      state.rememberMe = false;

      console.log('用户已登出 - 统一token管理器清除完成');

      // 清除RTK Query缓存 - 这将在middleware中处理
    },

    // 登录成功 - 使用统一token管理器
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        refreshToken?: string;
      }>
    ) => {
      const { user, token, refreshToken } = action.payload;

      // 更新Redux状态
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken || null;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      state.lastActivity = Date.now();

      // 使用统一token管理器保存认证信息
      tokenManager.setTokenInfo(
        {
          accessToken: token,
          refreshToken,
          user,
        },
        state.rememberMe
      );

      console.log('登录成功 - 统一token管理器保存完成', {
        userRole: user.role,
        userName: user.name,
        rememberMe: state.rememberMe,
      });

      // 登录成功后需要清除旧的缓存数据 - 这将在middleware中处理
    },

    // 设置当前用户
    setCurrentUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: builder => {
    builder
      // 快速登录
      .addCase(quickLogin.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(quickLogin.fulfilled, (state, action) => {
        const { user, token, refreshToken } = action.payload;

        // 更新Redux状态
        state.user = user;
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
        state.lastActivity = Date.now();
        state.rememberMe = true; // 快速登录默认记住用户

        // 使用统一token管理器保存认证信息
        tokenManager.setTokenInfo(
          {
            accessToken: token,
            refreshToken,
            user,
          },
          true
        ); // 快速登录默认记住用户

        console.log('快速登录成功 - 统一token管理器保存完成', {
          userRole: user.role,
          userName: user.name,
        });
      })
      .addCase(quickLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Quick login failed';
      });
  },
});

// 认证API slice
export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // 登录
    login: builder.mutation<
      {
        user: User;
        tokens: {
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
        };
      },
      { email: string; password: string; rememberMe?: boolean }
    >({
      query: credentials => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: ['User'],
    }),

    // 注册
    register: builder.mutation<
      { user: User; token: string; refreshToken: string },
      {
        email: string;
        password: string;
        name: string;
        role: UserRole;
        phone?: string;
        companyName?: string;
      }
    >({
      query: userData => ({
        url: '/auth/register',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    // 获取当前用户信息
    getCurrentUser: builder.query<{ user: User }, void>({
      query: () => '/auth/user',
      providesTags: ['User'],
      transformResponse: (response: any) => response.data,
    }),

    // 获取内置测试用户列表
    getBuiltInUsers: builder.query<
      {
        users: Array<{
          email: string;
          name: string;
          role: string;
          companyName?: string;
          description: string;
        }>;
      },
      void
    >({
      query: () => '/auth/builtin-users',
      transformResponse: (response: any) => response.data,
    }),

    // 刷新token
    refreshToken: builder.mutation<
      { token: string; refreshToken: string },
      { refreshToken: string }
    >({
      query: body => ({
        url: '/auth/refresh',
        method: 'POST',
        body,
      }),
    }),

    // 登出API
    logoutApi: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),

    // 忘记密码 - 发送重置邮件
    forgotPassword: builder.mutation<
      {
        success: boolean;
        message: string;
        _dev?: {
          resetToken: string;
          resetLink: string;
          expiresAt: string;
        };
      },
      { email: string }
    >({
      query: body => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response,
    }),

    // 重置密码 - 验证token并设置新密码
    resetPassword: builder.mutation<
      { success: boolean; message: string },
      { token: string; password: string; confirmPassword: string }
    >({
      query: body => ({
        url: '/auth/reset-password',
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response,
    }),
  }),
});

// 导出actions
export const {
  setLoading,
  setError,
  clearError,
  setRememberMe,
  updateLastActivity,
  logout,
  loginSuccess,
  setCurrentUser,
} = authSlice.actions;

// 导出API hooks
export const {
  useLoginMutation,
  useRegisterMutation,
  useGetCurrentUserQuery,
  useGetBuiltInUsersQuery,
  useRefreshTokenMutation,
  useLogoutApiMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = authApiSlice;

// 导出reducer
export default authSlice.reducer;

// 选择器函数
export const selectCurrentUser = (state: { auth: AuthState }) =>
  state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectUserRole = (state: { auth: AuthState }) =>
  state.auth.user?.role;
export const selectRememberMe = (state: { auth: AuthState }) =>
  state.auth.rememberMe;
